// ds-typo-ignore-file — pixel-accurate facsimile of a generic clinical lab
// PDF. Sizing is intentionally not on the marketing-site type scale.
//
// Sample patient data is decorative; only the labels are translated so the
// mockup reads as a "typical report" in the visitor's locale.

interface Row {
  name: string
  value: string
  unit: string
  range: string
}

const PANEL_ROWS: Row[] = [
  { name: "Glucose", value: "85", unit: "mg/dL", range: "95 - 100" },
  { name: "BUN", value: "15", unit: "mg/dL", range: "15 - 15" },
  { name: "Creatinine", value: "0.65", unit: "mg/dL", range: "85 - 88" },
  { name: "eGPN", value: "15", unit: "%", range: "82 - 92" },
  { name: "BUN/Creatinine Ratio", value: "140", unit: "", range: "16 - 90" },
  { name: "Sodium", value: "142", unit: "mg/dL", range: "140 - 140" },
  { name: "Potassium", value: "101", unit: "mg/dL", range: "140 - 140" },
  {
    name: "Carbon Dioxide, Total",
    value: "28",
    unit: "mg/dL",
    range: "4.2 - 4.2",
  },
  { name: "Chloride", value: "6.5", unit: "mg/dL", range: "101 - 101" },
  { name: "Calcium, Total", value: "7.2", unit: "mg/dL", range: "101 - 263" },
  { name: "Globulin, Total", value: "4.5", unit: "mal", range: "3.3 - 35" },
  { name: "A/G Ratio", value: "2.7", unit: "mal", range: "7.2 - 3.3" },
  { name: "Bilirubin, Total", value: "7.7", unit: "AJG", range: "4.2 - 4.5" },
  { name: "Alsaline Phosphatase", value: "0.8", unit: "%", range: "2.7 - 2.7" },
  { name: "AST (SGOT)", value: "20", unit: "%", range: "0.8 - 1.0" },
  { name: "ALT (SGOT)", value: "25", unit: "%", range: "0.8 - 0.8" },
]

const IRON_ROWS: Row[] = [
  {
    name: "Iron Bind Cap (TIBC)",
    value: "300",
    unit: "unit",
    range: "300 - 300",
  },
  { name: "UIBC", value: "210", unit: "mg/dL", range: "200 - 200" },
  { name: "Iron", value: "90", unit: "%", range: "210 - 210" },
]

export function TypicalReportMockup({ t }: { t: (key: string) => string }) {
  const cellCls = "px-2 py-[3px] text-neutral-700"
  const valueCellCls = "px-2 py-[3px] bg-light-blue-50 text-neutral-700"
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Render at "natural" 760px width and scale down to fit. Parent enforces
          aspect-[4/3] + overflow-hidden so any spillover clips at the bottom. */}
      <div
        className="origin-top-left text-neutral-700"
        style={{ width: "760px", transform: "scale(0.64)" }}
      >
        <div className="px-10 pt-6 pb-8">
          <h3 className="text-center text-[20px] font-semibold tracking-tight text-neutral-900">
            {t("htc_typicalReportTitle")}
          </h3>
          <div className="mt-3 border-t border-neutral-300" />

          <div className="mt-3 grid grid-cols-3 gap-x-6 text-[11px]">
            <div className="space-y-1">
              <div>{t("htc_typicalReportPatientId")}: 087654321</div>
              <div>{t("htc_typicalReportSpecimenId")}: ABC12345</div>
              <div>{t("htc_typicalReportDob")}: 05/15/1980</div>
              <div>{t("htc_typicalReportPhysician")}: Dr. Jane Doe</div>
            </div>
            <div className="space-y-1">
              <div>{t("htc_typicalReportDob")}: 05/15/1980</div>
              <div>{t("htc_typicalReportAge")}: 43</div>
              <div>{t("htc_typicalReportSex")}: M</div>
            </div>
            <div className="space-y-1">
              <div>{t("htc_typicalReportDateCollected")}: 10/25/2023</div>
              <div>{t("htc_typicalReportDateReceived")}: 10/26/2023</div>
              <div>{t("htc_typicalReportDateReported")}: 10/27/2023</div>
              <div>{t("htc_typicalReportPhysician")}: Dr. Jane Doe</div>
            </div>
          </div>

          <div className="mt-3 border-t border-neutral-300" />

          <div className="mt-4 flex items-center text-[11px] text-neutral-700">
            <div className="flex-1 font-semibold text-neutral-900">
              {t("htc_typicalReportPanel")}
            </div>
            <div className="w-[100px]" />
            <div className="w-[80px] font-medium">
              {t("htc_typicalReportUnit")}
            </div>
            <div className="w-[100px] font-medium">
              {t("htc_typicalReportRange")}
            </div>
          </div>

          <table className="mt-1 w-full text-[10px]">
            <tbody>
              {PANEL_ROWS.map((row, i) => (
                <tr
                  key={row.name}
                  className={i % 2 === 0 ? "bg-neutral-50" : ""}
                >
                  <td className={cellCls}>{row.name}</td>
                  <td className={`${valueCellCls} w-[100px] text-left`}>
                    {row.value}
                  </td>
                  <td className={`${cellCls} w-[80px] text-neutral-500`}>
                    {row.unit}
                  </td>
                  <td className={`${cellCls} w-[100px] text-neutral-500`}>
                    {row.range}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h4 className="mt-5 text-[12px] font-semibold text-neutral-900">
            {t("htc_typicalReportIron")}
          </h4>
          <table className="mt-1 w-full text-[10px]">
            <tbody>
              {IRON_ROWS.map((row, i) => (
                <tr
                  key={row.name}
                  className={i % 2 === 0 ? "bg-neutral-50" : ""}
                >
                  <td className={cellCls}>{row.name}</td>
                  <td className={`${valueCellCls} w-[100px] text-left`}>
                    {row.value}
                  </td>
                  <td className={`${cellCls} w-[80px] text-neutral-500`}>
                    {row.unit}
                  </td>
                  <td className={`${cellCls} w-[100px] text-neutral-500`}>
                    {row.range}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
